/**
 * match-api.ts - API client for match operations
 *
 * Handles all HTTP requests related to match results and management.
 */

import type {
  Match,
  AddMatchRequest,
  UpdateMatchResultRequest,
  ApiError
} from '../../types/api';
import { API_BASE_URL } from '../utils/constants';

/**
 * Add a match result to a tournament
 * @param tournamentId - Tournament ID
 * @param matchData - Match data
 * @returns Created match object
 * @throws Error if request fails
 */
export async function addMatchAPI(
  tournamentId: string,
  matchData: AddMatchRequest
): Promise<Match> {
  const response = await fetch(`${API_BASE_URL}/${tournamentId}/matches`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(matchData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to add match');
  }

  return await response.json();
}

/**
 * Update a match result
 * @param matchId - Match ID
 * @param resultData - Result data
 * @returns Updated match object
 * @throws Error if request fails
 */
export async function updateMatchResultAPI(
  matchId: string,
  resultData: UpdateMatchResultRequest
): Promise<Match> {
  const response = await fetch(`${API_BASE_URL}/matches/${matchId}/result`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(resultData)
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error || 'Failed to update match result');
  }

  return await response.json();
}
